import { type ReactNode, useEffect } from 'react';
import { useLocation } from 'react-router';

import type { ApiISODateTimeString } from '#/api/types/ApiTypedDate';
import { useAppConfig } from '#/config/AppConfigContext';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { I18nLocale } from '#/i18n/types/I18nLocale';
import { HORIZON_ENABLED } from '#/rendering/state/renderingState';
import { getStaticImageSrc } from '#/utils/getImageSrc';

type Props = {
  title: string;
  description?: string | null;
  children: ReactNode;
  ogImage?: string | { url: string; alt: string | null } | null;
  robotsNoIndex?: boolean;
  rssFeed?: { title: string; href: string } | null;
  article?: {
    firstPublished: ApiISODateTimeString | null;
    lastModified: ApiISODateTimeString | null;
    author: string | null;
    tags: string[];
  } | null;
  translations?: {
    items: Array<{ url: string; locale: I18nLocale }>;
    /**
     * There must be a reference page
     */
    xDefault: string;
  } | null;
  schemaMarkups?: (string | null | undefined)[];
};

export function RouteModule({
  title,
  description,
  children,
  ogImage,
  robotsNoIndex,
  rssFeed,
  article,
  translations,
  schemaMarkups,
}: Props) {
  const { appConfig } = useAppConfig();
  const { i18n } = useI18n();
  const location = useLocation();
  const isInStaffRoute =
    location.pathname.startsWith('/staff') ||
    location.pathname.startsWith('/cms');

  const affixedTitle = isInStaffRoute ? '🧑‍💼 ' + title : `${title} - Idealist`;

  useEffect(() => {
    if (HORIZON_ENABLED) return;
    document.title = affixedTitle;
  }, [affixedTitle]);

  // oxlint-disable-next-line jsx-no-useless-fragment
  if (!HORIZON_ENABLED) return <>{children}</>;

  const ogImageUrl = typeof ogImage === 'string' ? ogImage : ogImage?.url;

  return (
    <>
      {(!appConfig.idealist.allowRobots || robotsNoIndex) && (
        <meta name="robots" content="noindex, nofollow" />
      )}

      <title>{affixedTitle}</title>
      <meta property="og:title" content={title} />

      <meta
        name="description"
        content={
          description ??
          i18n(
            'Idealist connects millions of idealists – people who want to do good – with opportunities for action and collaboration all over the world.',
          )
        }
      />

      <meta name="twitter:card" content="summary_large_image" />

      <meta
        property="og:image"
        content={
          ogImageUrl ??
          getStaticImageSrc('og-image.jpg', appConfig.idealist.staticImgPath)
        }
      />
      {ogImage && 'alt' && typeof ogImage === 'object' && ogImage.alt && (
        <meta property="og:image:alt" content={ogImage.alt} />
      )}

      {rssFeed && (
        <link
          rel="alternate"
          type="application/rss+xml"
          title={rssFeed.title}
          href={rssFeed.href}
        />
      )}

      {article && (
        <>
          {article.firstPublished && (
            <meta
              property="article:published_time"
              content={article.firstPublished}
            />
          )}

          {article.lastModified && (
            <meta
              property="article:modified_time"
              content={article.lastModified}
            />
          )}

          {article.author && (
            <>
              <meta property="article:author" content={article.author} />
              <meta
                property="profile:first_name"
                content={article.author.split(' ')[0]}
              />
              <meta
                property="profile:last_name"
                content={article.author.split(' ').pop()}
              />
            </>
          )}

          {article.tags.map((tag) => (
            <meta key={tag} property="article:tag" content={tag} />
          ))}
        </>
      )}

      {translations && (
        <>
          {translations.items.map((translation) => (
            <link
              key={translation.locale}
              rel="alternate"
              href={translation.url}
              hrefLang={translation.locale}
            />
          ))}

          <link
            rel="alternate"
            href={translations.xDefault}
            hrefLang="x-default"
          />
        </>
      )}

      {schemaMarkups?.map(
        (markup, index) =>
          markup && (
            <script
              // oxlint-disable-next-line no-array-index-key
              key={index}
              type="application/ld+json"
            >
              {markup}
            </script>
          ),
      )}

      {children}
    </>
  );
}
